import type { Column } from "@tanstack/react-table";
import { isEmpty } from "common/utils/isEmpty";
import type { ReactNode } from "react";
import { useEffect } from "react";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/Checkbox";
import useLocalStorage from "ui/hooks/useLocalStorage";
import OverflowMenu from "ui/popovers/OverflowMenu";
import type { DataTableColumn } from "./types";

export default function ShowHideColumns<T>({ columns, storageKey }: { columns: Column<T>[]; storageKey?: string }) {
  // Save column visibility in localStorage - prefers a stable key, else falls back to a key concatenated from column ids
  const [columnVisibility, setColumnVisibility] = useLocalStorage<Record<string, boolean>>(
    storageKey ?? columns.map((column) => column.id).join("-"),
    {},
  );

  // Re-apply saved visibility when the saved map OR the column set changes. Custom-field columns
  // load asynchronously (after the builder-account fetch), so they mount after the initial restore;
  // depending only on columnVisibility would never apply saved prefs to those late-arriving columns.
  const columnIdsKey = columns.map((column) => column.id).join("-");
  useEffect(() => {
    if (isEmpty(columnVisibility)) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      columns.forEach((column) => {
        const saved = columnVisibility[column.id];
        if (typeof saved === "boolean" && column.getIsVisible() !== saved) {
          column.toggleVisibility(saved);
        }
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [columnVisibility, columnIdsKey]);

  return (
    <OverflowMenu
      title="Column Visibility"
      showCloseIcon
      menuItems={columns.map((column) => ({
        onClick: () => {
          const isVisible = column.getIsVisible();
          column.toggleVisibility();
          setColumnVisibility({ ...columnVisibility, [column.id]: !isVisible });
        },
        children: (
          <Checkbox
            key={column.id}
            checked={column.getIsVisible()}
            label={column.columnDef.header as ReactNode}
            data-testid={`column-visibility-checkbox-${column.id}`}
          />
        ),
      }))}
    >
      <Button
        btnStyle="secondary"
        btnSize="medium"
        onClick={() => {
          columns.forEach((column) => column.toggleVisibility(!(column.columnDef as DataTableColumn<T>).hide));
          setColumnVisibility({});
        }}
        dataTestId="reset-columns-button"
      >
        Reset to Default
      </Button>
    </OverflowMenu>
  );
}
